import React, {
  useState,
} from "react";

import {
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaIdCard,
  FaPen,
  FaTrash,
} from "react-icons/fa";

import RolePill from "../ui/RolePill";

import {
  ActeursPreview,
  BadgesPreview,
  ClubsPreview,
  CoachBadgeStatusControls,
  UserIdentity,
} from "./UserCells";

export default function UsersCardsMobile({
  rows,
  loading,
  badgesByUser,

  onEdit,
  onDelete,
  onLinkClub,
  onMessage,

  onCoachBadge,
  exportingCoachBadgeUserId,

  onToggleCoachBadgePrinted,
  onToggleCoachBadgeReceived,
  coachBadgeUpdatingByUser,
}) {
  const [
    expandedIds,
    setExpandedIds,
  ] = useState([]);

  const toggleExpanded = (
    userId,
  ) => {
    setExpandedIds(
      (previousIds) =>
        previousIds.includes(
          userId,
        )
          ? previousIds.filter(
              (id) =>
                id !== userId,
            )
          : [
              ...previousIds,
              userId,
            ],
    );
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-500 shadow-sm lg:hidden dark:border-slate-800 dark:bg-slate-900">
        Chargement des utilisateurs…
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-500 shadow-sm lg:hidden dark:border-slate-800 dark:bg-slate-900">
        Aucun utilisateur ne correspond aux filtres.
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:hidden">
      {rows.map((user) => {
        const expanded =
          expandedIds.includes(
            user.id,
          );

        const userBadges =
          badgesByUser[user.id] ||
          [];

        const canGenerateCoachBadge =
          userBadges.length > 0;

        const isCoachBadgeExporting =
          Number(
            exportingCoachBadgeUserId,
          ) === Number(user.id);

        const updatingField =
          coachBadgeUpdatingByUser?.[
            user.id
          ] || null;

        const showCoachTracking =
          canGenerateCoachBadge ||
          Boolean(
            user.coach_badge_printed,
          ) ||
          Boolean(
            user.coach_badge_received,
          );

        return (
          <article
            key={user.id}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            {/* Identité */}
            <UserIdentity
              user={user}
            />

            {/* Rôle */}
            <div className="mt-2">
              <RolePill
                roleId={
                  user.role_id
                }
              />
            </div>

            {/* Suivi carte coach */}
            {showCoachTracking ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-950/50">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Suivi carte coach
                </div>

                <CoachBadgeStatusControls
                  user={user}
                  canManageCoachBadge={
                    canGenerateCoachBadge
                  }
                  onTogglePrinted={
                    onToggleCoachBadgePrinted
                  }
                  onToggleReceived={
                    onToggleCoachBadgeReceived
                  }
                  updatingField={
                    updatingField
                  }
                  compact
                />
              </div>
            ) : null}

            {/* Actions principales */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() =>
                  onEdit(user)
                }
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                <FaPen />
                Éditer
              </button>

              <button
                type="button"
                onClick={() =>
                  toggleExpanded(
                    user.id,
                  )
                }
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {expanded ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}

                {expanded
                  ? "Réduire"
                  : "Détails"}
              </button>

              {onCoachBadge &&
              canGenerateCoachBadge ? (
                <button
                  type="button"
                  onClick={() =>
                    onCoachBadge(
                      user,
                    )
                  }
                  disabled={
                    isCoachBadgeExporting
                  }
                  className="col-span-2 inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-blue-950 px-3 text-sm font-semibold text-white transition hover:bg-blue-900 disabled:cursor-wait disabled:opacity-60"
                >
                  <FaIdCard
                    className={
                      isCoachBadgeExporting
                        ? "animate-pulse"
                        : ""
                    }
                  />

                  {isCoachBadgeExporting
                    ? "Génération..."
                    : "Générer la carte coach"}
                </button>
              ) : null}
            </div>

            {/* Détails */}
            {expanded ? (
              <div className="mt-4 space-y-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                {/* Clubs */}
                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Clubs
                  </h3>

                  <ClubsPreview
                    user={user}
                    onLinkClub={
                      onLinkClub
                    }
                    max={99}
                  />
                </section>

                {/* Acteurs */}
                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Acteurs
                  </h3>

                  <ActeursPreview
                    user={user}
                    max={99}
                  />
                </section>

                {/* Badges formation */}
                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Badges
                  </h3>

                  <BadgesPreview
                    badges={
                      userBadges
                    }
                    small
                  />
                </section>

                {/* Message */}
                <button
                  type="button"
                  onClick={() =>
                    onMessage(user)
                  }
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  <FaEnvelope />
                  Envoyer un message
                </button>

                {/* Suppression */}
                <button
                  type="button"
                  onClick={() =>
                    onDelete(
                      user.id,
                    )
                  }
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                >
                  <FaTrash />
                  Supprimer
                </button>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}